import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  ArrowBack,
  Visibility,
  VisibilityOff,
  School,
  VpnKey,
  CheckCircle,
  Home,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { changePassword } from "../api/authApi";

// Academic Theme
const academicTheme = createTheme({
  palette: {
    primary: {
      main: "#1565C0",
    },
    secondary: {
      main: "#FF8F00",
    },
    background: {
      default: "#F8F9FA",
      paper: "#FFFFFF",
    },
  },
});

const ChangePasswordPage = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openModal, setOpenModal] = useState(false);

  // State สำหรับเปิด/ปิดรหัสผ่าน
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  // ฟังก์ชันกรอง input ห้ามพิมพ์ภาษาไทย
  const filterNoThai = (value) => {
    return value.replace(/[\u0E00-\u0E7F]/g, "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("รหัสผ่านใหม่ไม่ตรงกัน");
      return;
    }

    try {
      await changePassword(oldPassword, newPassword);
      setSuccess("เปลี่ยนรหัสผ่านเรียบร้อยแล้ว");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setOpenModal(true); // เปิด modal แจ้งไป login ใหม่
    } catch (err) {
      setError(err.response?.data?.message || "เกิดข้อผิดพลาด");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    localStorage.removeItem("access_token");
    localStorage.removeItem("username");
    navigate("/login");
  };

  return (
    <ThemeProvider theme={academicTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #F8F9FA 0%, #E3F2FD 100%)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Navigation Bar */}
        <AppBar position="static" sx={{ backgroundColor: "#1565C0" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate("/profile")}
              sx={{ mr: 2 }}
            >
              <ArrowBack />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
              <School sx={{ fontSize: 32, mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                เปลี่ยนรหัสผ่าน
              </Typography>
            </Box>

            <Button
              startIcon={<Home />}
              sx={{ color: "white" }}
              onClick={() => navigate("/dashboard")}
            >
              หน้าหลัก
            </Button>
          </Toolbar>
        </AppBar>

        <Container
          maxWidth="sm"
          sx={{
            flexGrow: 1,
            py: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 4,
              boxShadow: "0 8px 32px rgba(21,101,192,0.15)",
              width: "100%",
              maxWidth: 500,
              bgcolor: "background.paper",
              border: "1px solid #E3F2FD",
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 3,
                  boxShadow: "0 4px 16px rgba(21,101,192,0.3)",
                }}
              >
                <VpnKey sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1565C0",
                  mb: 1,
                }}
              >
                เปลี่ยนรหัสผ่าน
              </Typography>
              <Typography variant="body1" sx={{ color: "#666" }}>
                กรุณากรอกข้อมูลเพื่อเปลี่ยนรหัสผ่านใหม่
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 3 }}
            >
              <TextField
                label="รหัสผ่านเดิม"
                type={showOldPassword ? "text" : "password"}
                fullWidth
                required
                value={oldPassword}
                onChange={(e) => setOldPassword(filterNoThai(e.target.value))}
                autoComplete="current-password"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                        tabIndex={-1}
                        aria-label={
                          showOldPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"
                        }
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="รหัสผ่านใหม่"
                type={showNewPassword ? "text" : "password"}
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(filterNoThai(e.target.value))}
                autoComplete="new-password"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                        tabIndex={-1}
                        aria-label={
                          showNewPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"
                        }
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) =>
                  setConfirmPassword(filterNoThai(e.target.value))
                }
                autoComplete="new-password"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                        tabIndex={-1}
                        aria-label={
                          showConfirmPassword ? "ซ่อนรหัสผ่าน" : "แสดงรหัสผ่าน"
                        }
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{
                    borderRadius: 3,
                    "& .MuiAlert-message": {
                      fontWeight: 600,
                    },
                  }}
                >
                  {success}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  py: 1.5,
                  mt: 2,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 3,
                  textTransform: "none",
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)",
                  boxShadow: "0 4px 16px rgba(21,101,192,0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)",
                    boxShadow: "0 6px 20px rgba(21,101,192,0.4)",
                  },
                }}
              >
                ยืนยันการเปลี่ยนรหัสผ่าน
              </Button>
            </Box>
          </Paper>
        </Container>

        {/* Success Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          PaperProps={{
            sx: {
              borderRadius: 4,
              p: 4,
              bgcolor: "background.paper",
              boxShadow: "0 8px 32px rgba(21,101,192,0.2)",
              minWidth: 400,
              textAlign: "center",
              border: "1px solid #E3F2FD",
            },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
                boxShadow: "0 4px 16px rgba(46,125,50,0.3)",
              }}
            >
              <CheckCircle sx={{ fontSize: 40, color: "white" }} />
            </Box>

            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1565C0", mb: 2 }}
            >
              เปลี่ยนรหัสผ่านสำเร็จ
            </Typography>

            <Typography variant="body1" sx={{ color: "#666", lineHeight: 1.6 }}>
              รหัสผ่านของคุณถูกเปลี่ยนเรียบร้อยแล้ว
              <br />
              กรุณาเข้าสู่ระบบใหม่ด้วยรหัสผ่านใหม่
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={handleCloseModal}
            size="large"
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 600,
              borderRadius: 3,
              textTransform: "none",
              background: "linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)",
              boxShadow: "0 4px 16px rgba(46,125,50,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1B5E20 0%, #4CAF50 100%)",
                boxShadow: "0 6px 20px rgba(46,125,50,0.4)",
              },
            }}
            autoFocus
          >
            ไปที่หน้าเข้าสู่ระบบ
          </Button>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
};

export default ChangePasswordPage;
